import { ASKED_RATINGS, SLIDER_UPDATE_MINUTES, moodColor, moodLabel } from '@adhd/shared';
import { CrisisNoticeCard, type CrisisNotice } from '../components/CrisisNotice.js';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { SLIDER_KEYS } from '../components/RatingSlider.js';
import { CheckinRow, type LastCheckin } from '../components/CheckinRow.js';

/**
 * Quick check-in.
 *
 * Everything except mood is optional and folded away behind "add more detail".
 * A check-in that feels like a form is a check-in that stops happening by week
 * two — and inconsistent data from a habit that survives beats rich data from
 * one that dies.
 */

const CONTEXT_TAGS = [
  'work', 'home', 'social', 'alone', 'post-meeting', 'no sleep',
  'skipped meds', 'hungry', 'overstimulated', 'deadline', 'conflict', 'exercise',
];

/*
 * FOUR QUESTIONS, NOT SEVEN. The list is in @adhd/shared, because the page he
 * SHARES names them too and a second copy would be a second statement of the
 * same rule. See ASKED_RATINGS there for why these four.
 */
const DIMENSIONS = ASKED_RATINGS;


// Which keys count as an answer on a slider: see RatingSlider.

/**
 * The check-in — and, since release 5, the top of Today.
 *
 * ONE COMPONENT FOR BOTH. Today used to carry a row reading "How are you
 * doing? · 15 sec" that travelled to this screen, so the thing the app exists
 * for was one navigation away from the screen it opens on. Inline it is two
 * gestures: drag the thumb, tap Save.
 *
 * `inline` drops the page heading and the "Not now" way out, because on Today
 * there is nothing to go back to — the rest of the day is underneath it.
 */
export function CheckIn({
  inline = false,
  onSaved,
  last = null,
  startOpen = false,
  count = 0,
  day,
}: {
  inline?: boolean;
  /** The day Today is showing — before 4am, yesterday (14 Oct 2026). Inline only. */
  day?: string;
  onSaved?: () => void;
  /**
   * How many check-ins today already, so ONE of the four optional ratings is
   * asked in the open each time, in turn (release 3 of 2.0, docs/PRD-2.0.md
   * §8). Four dimensions at a quarter of the check-in rate instead of a
   * seventh: in 77 check-ins the fold yielded 12, 12, 10 and 5 answers.
   */
  count?: number;
  /**
   * Open the form at once, inline. Today's slider (RateNow) is the check-in
   * since release 1 of 2.0; this form is reached from its "add a note" and
   * arrives already open, because the tap that got here was the request.
   */
  startOpen?: boolean;
  /**
   * The day's most recent check-in, or null for a day with none.
   *
   * Only read while collapsed, to say what was last said instead of asking
   * again. `/mood/today` returns the day newest-first, so this is `items[0]`.
   */
  last?: LastCheckin | null;
} = {}): JSX.Element {
  const navigate = useNavigate();
  /*
   * CLOSED UNTIL TOUCHED, which is the whole of this change.
   *
   * Release 5 put the form itself at the top of Today, and it stayed there all
   * day: after a save `reset()` empties every field and this component stays
   * mounted, so a day with three check-ins on it opened with an untouched
   * slider reading "Not said yet" and an empty note box. Three states of the
   * screen, one picture. Nick: "a slider in my face 24/7 with a text box."
   *
   * It opens IN PLACE. The row this replaces is not the row release 5 deleted
   * -- that one was a <Link> to /check-in, and the reason it went is that "at
   * 11:40pm on a bad day that navigation is where people stop". Nothing here
   * changes route, nothing unmounts, and a draft survives because the
   * component never goes away.
   *
   * Never collapsed on the /check-in deep link: arriving there is an explicit
   * request to check in, and a row saying "How are you doing?" on a page whose
   * heading already asks it would be a tap that undoes itself.
   */
  const [open, setOpen] = useState(!inline || startOpen);
  /*
   * Focus follows the disclosure.
   *
   * The row unmounts when it opens, so without this the focused element
   * vanishes and `document.activeElement` falls back to <body> -- no outline,
   * nothing announced, no sign the one action the app exists for did
   * anything. Only when the row was the thing that opened it: arriving on
   * /check-in, or a draft restoring, must not steal focus or raise a keyboard.
   */
  const openedByRow = useRef(false);
  useEffect(() => {
    if (!open || !openedByRow.current) return;
    openedByRow.current = false;
    document.getElementById('mood')?.focus();
  }, [open]);
  /*
   * Null until the slider is moved, and that is the whole point.
   *
   * It was `useState(0)` with no touched flag, so opening this screen, pressing
   * Save and leaving recorded a real mood of "Neutral" — indistinguishable, in
   * the mean, in the chart and on the clinician's page, from somebody who had
   * actually considered it and landed on zero. Migration 021 already argues
   * this about a model's suggested mood: "a number you did not give looks
   * identical to one you did once it is in your trend chart". It applies with
   * more force to the app's own default, because that one fires silently.
   *
   * The slider still SITS at zero — it has to sit somewhere — and reads
   * "Not said yet" until it is touched, at which point Save turns on.
   *
   * Gating the button rather than storing a null mood, deliberately. A check-in
   * without a mood would be a new kind of row: every average, every chart and
   * every count in the app assumes a check-in has one, and inventing a second
   * meaning for "check-in" to fix a default is a worse trade than one disabled
   * button. Nothing is lost — a person who opens this and cannot name anything
   * has not made a check-in, and the app should not write one for them.
   */
  const [mood, setMood] = useState<number | null>(null);
  /*
   * ONE SLIDER, AT THE TOP (14 Oct 2026). Inline on Today the slider above
   * this form IS the check-in, so this form no longer carries its own: it
   * takes its mood from the day's latest check-in. Within the slider's
   * revision window it ADDS to that check-in; past it, it makes a new one at
   * that word. Only with no check-in on the day at all does the mood slider
   * appear here, because there is nothing to take the word from.
   */
  const attachTo = inline && last?.id && Date.now() - Date.parse(last.recordedAt) < SLIDER_UPDATE_MINUTES * 60_000 ? last : null;
  const sliderShown = !inline || !last;
  const moodForSave = mood ?? (inline && last ? last.mood : null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);
  const [expanded, setExpanded] = useState(false);
  /*
   * The note is a question, not a box, until it is asked for.
   *
   * It stays in second place, which it earned on evidence -- 27 of 67
   * check-ins carry one, more than any of the seven sliders managed -- but an
   * empty textarea standing open is the other half of what he was looking at.
   * Tapping the row is the same tap that focuses the textarea today, so a
   * written check-in costs no extra gesture.
   */
  const [noteOpen, setNoteOpen] = useState(false);
  const [dimensions, setDimensions] = useState<Record<string, number>>({});
  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const asked = DIMENSIONS[count % DIMENSIONS.length];
  const rest = DIMENSIONS.filter((d) => d.key !== asked?.key);

  /* Anything they have actually authored, as opposed to defaults sitting where
     they were rendered. Only these are worth protecting. */
  const authored =
    note.trim().length > 0 || tags.length > 0 || Object.keys(dimensions).length > 0;

  /*
   * The draft survives leaving the screen.
   *
   * Held per-session and per-device only: this is unsaved writing about how
   * somebody is, and it has no business being persisted beyond the tab it was
   * typed in. Cleared the moment it becomes a real check-in.
   */
  useEffect(() => {
    if (!authored && mood === null) return;
    try {
      sessionStorage.setItem('checkin-draft', JSON.stringify({ mood, dimensions, tags, note }));
    } catch { /* private mode, a full quota — the draft is a nicety */ }
  }, [authored, mood, dimensions, tags, note]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('checkin-draft');
      if (!raw) return;
      const d = JSON.parse(raw) as Partial<{
        mood: number | null; dimensions: Record<string, number>; tags: string[]; note: string;
      }>;
      /* A restored draft OPENS the form. Unsaved writing behind a collapsed
         row is writing nobody can see, and the draft exists precisely because
         leaving the screen should not lose it. */
      if (typeof d.mood === 'number') { setMood(d.mood); setOpen(true); }
      if (d.dimensions && Object.keys(d.dimensions).length > 0) { setDimensions(d.dimensions); setExpanded(true); setOpen(true); }
      if (d.tags?.length) { setTags(d.tags); setExpanded(true); setOpen(true); }
      /* `setNoteOpen(true)` is the whole of a shipped bug. Without it the note
         was restored into state, the textarea was not mounted because the note
         is a closed question now, and the row rendered byte-identical to an
         empty one -- while `save()` still sent `note` regardless. So a
         paragraph about how he is went into a check-in having never appeared
         on screen, and he pressed Save on what looked like a mood-only form.
         Before the row existed the textarea was unconditional, so restoring a
         note always showed it; this was introduced with the closed question. */
      if (d.note) { setNote(d.note); setNoteOpen(true); setExpanded(true); setOpen(true); }
    } catch { /* nothing to restore */ }
  }, []);

  function toggleTag(tag: string): void {
    setTags(tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag]);
  }

  /**
   * Back to rest, after a check-in is written and the screen stays up.
   *
   * ONE DEFINITION, because there are two ways out of `save()` that both need
   * it and they had drifted: the ordinary path reset the form and the crisis
   * path did not, so the one case where a second identical row matters most
   * was the one case that allowed it.
   */
  function reset(): void {
    setBusy(false);
    setMood(null);
    setNote('');
    setTags([]);
    setDimensions({});
    setExpanded(false);
    setNoteOpen(false);
    /* Back to the row. On the crisis path this does NOT hide the notice: the
       collapsed branch below requires `!crisis`, and `setCrisis` runs before
       this in the same batch. */
    setOpen(!inline);
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      /* Unreachable while Save is gated on a touched mood, and it still clears
         `busy` — an early return that leaves a spinner running is the bug this
         function just had. */
      if (moodForSave === null) { setBusy(false); return; }
      try { sessionStorage.removeItem('checkin-draft'); } catch { /* nothing held */ }
      const saved = attachTo?.id
        ? await api.updateCheckin(attachTo.id, {
            ...dimensions,
            ...(tags.length > 0 ? { contextTags: tags } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
          })
        : await api.createCheckin({
        ...(day ? { day } : {}),
        mood: moodForSave,
        energy: dimensions.energy ?? null,
        focus: dimensions.focus ?? null,
        restlessness: dimensions.restlessness ?? null,
        overwhelm: dimensions.overwhelm ?? null,
        irritability: dimensions.irritability ?? null,
        rejectionSensitivity: dimensions.rejectionSensitivity ?? null,
        anxiety: dimensions.anxiety ?? null,
        contextTags: tags,
        note: note.trim() || null,
        source: 'manual',
        // A check-in made here stands on its own — the pairing exists only when
        // one is confirmed from an entry.
        voiceLogId: null,
      });
      /*
       * Saved either way — the only question is whether we leave.
       *
       * Navigating straight home would put the numbers on a screen the person
       * has already left, which is the same as not showing them. So the check-in
       * stays up with the card under it, and going home becomes their decision.
       */
      /*
       * THE FORM COMES BACK TO REST HERE TOO.
       *
       * This branch used to `return` before the reset below, so after a note
       * that trips the crisis check the mood, the note and the chips were all
       * still sitting there and Save went live again over a check-in that had
       * already been written. The crisis card is tall and pushes Save out of
       * view, so you scroll, find "Save check-in" waiting, and tap it — and
       * get a second identical row. The holistic pass did exactly that and
       * produced two rows thirty-one seconds apart, same mood, same note.
       *
       * The duplicate then travels: it inflates the check-in count and the
       * day-end figure that reach a share recipient, at the one moment the
       * record most needs to be right.
       *
       * `onSaved()` is called too, so Today's own counts move. The card stays
       * up — it is the whole point of not navigating away — but the thing
       * under it is now a form at rest rather than a loaded gun.
       */
      if (saved.crisis) {
        setCrisis(saved.crisis);
        reset();
        if (onSaved) onSaved();
        return;
      }
      if (onSaved) {
        /*
         * BACK TO REST, because this component is still on screen.
         *
         * The success path used to be `navigate('/')` and nothing else, which
         * unmounted the whole thing — so `busy` never needed clearing and the
         * fields never needed emptying. Inline on Today it stays mounted, and
         * without this the spinner runs for ever on a check-in that has
         * already been written: the server answers 201 in three milliseconds
         * and the button never comes back. Nothing is lost and it looks like
         * everything is.
         *
         * The fields are emptied too. The mood goes back to null so Save is
         * gated again — otherwise the next tap would save the same number a
         * second time — and the note goes because it has been written down.
         * What was just saved is on the screen underneath, in Today so far.
         */
        reset();
        onSaved();
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
      setBusy(false);
    }
  }

  /*
   * THE ROW. One tap target, and the only thing this screen asks for until it
   * is tapped.
   *
   * `!crisis` is load-bearing: the crisis notice is rendered inside the form
   * below, and the crisis save path calls `reset()`, which closes the form.
   * Collapsing over the notice would hide crisis resources at the one moment
   * they exist for.
   */
  if (inline && !open && !crisis) {
    return (
      <CheckinRow
        last={last}
        onOpen={() => {
          openedByRow.current = true;
          setOpen(true);
        }}
      />
    );
  }
  return (
    <div className="stack-lg stack">
      {!inline && (
        <header className="page-header">
          <h1>How are you doing?</h1>
          <p>Just the one slider is plenty. The rest is there if you want it.</p>
        </header>
      )}

      {inline && last && (
        <p className="muted" style={{ margin: 0 }}>
          {attachTo
            ? `Adding to your ${new Date(last.recordedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} check-in — ${moodLabel(last.mood)}.`
            : `A new check-in at ${moodLabel(last.mood)}, with what you add here.`}
        </p>
      )}
      {sliderShown && (
      <div className="card stack">
        <div className="field">
          <label htmlFor="mood">Overall right now</label>
          <input
            id="mood"
            className="slider"
            type="range"
            min={-5}
            max={5}
            step={1}
            value={mood ?? 0}
            // `input` rather than `change`: dragging to the value the slider
            // already sits on fires no change event, so recording a genuine
            // zero used to take two gestures — out and back.
            onInput={(e) => setMood(Number((e.target as HTMLInputElement).value))}
            onPointerUp={(e) => setMood(Number(e.currentTarget.value))}
            onKeyUp={(e) => {
              if (SLIDER_KEYS.has(e.key)) setMood(Number(e.currentTarget.value));
            }}
          />
          <div className="slider-scale">
            <span className="faint">Awful &minus;5</span>
            <strong
              style={{
                fontSize: 'var(--text-lg)',
                color: mood === null ? 'var(--text-muted)' : moodColor(mood),
              }}
            >
              {mood === null ? 'Not said yet' : `${moodLabel(mood)} · ${mood > 0 ? '+' : ''}${mood}`}
            </strong>
            <span className="faint">+5 Great</span>
          </div>
        </div>
      </div>
      )}


      {/*
        THE NOTE IS THE SECOND FIELD, and it was the last.
        
        27 of 67 check-ins carry one — more than any of the seven sliders
        managed, and three check-ins in four filled none of those at all. It
        sat behind all seven and twelve chips, labelled "Anything else", which
        is a box offered rather than a question asked.
        
        A question gets answered; a box gets skipped.
      */}
      {noteOpen ? (
        <div className="field">
          <label htmlFor="note">What&rsquo;s going on?</label>
          <textarea
            id="note"
            className="textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional. A sentence is fine."
            autoFocus
          />
        </div>
      ) : (
        <button className="ask-row" onClick={() => setNoteOpen(true)}>
          <span>What&rsquo;s going on?</span>
          <span className="faint">optional &rsaquo;</span>
        </button>
      )}

      {/* ONE MORE, IN THE OPEN, ROTATING — release 3 of 2.0. The other three
          stay behind the fold. Skipping is doing nothing: an untouched slider
          saves as blank, as it always has. */}
      {asked && (
        <section className="card stack-sm stack rotating-ask">
          <div className="row-between">
            <label htmlFor={asked.key} style={{ margin: 0 }}>
              One more, this time: <strong>{asked.label}</strong>
              {dimensions[asked.key] !== undefined && <span className="faint"> · {dimensions[asked.key]}</span>}
            </label>
            {dimensions[asked.key] === undefined ? (
              <span className="faint">skip if unsure</span>
            ) : (
              <button
                type="button"
                className="btn-inline"
                onClick={() =>
                  setDimensions((prev) => {
                    const next = { ...prev };
                    delete next[asked.key];
                    return next;
                  })
                }
              >
                clear
              </button>
            )}
          </div>
          <input
            id={asked.key}
            className="slider"
            type="range"
            min={1}
            max={5}
            step={1}
            value={dimensions[asked.key] ?? 3}
            onChange={(e) => setDimensions({ ...dimensions, [asked.key]: Number(e.target.value) })}
            onPointerUp={(e) => {
              const value = Number(e.currentTarget.value);
              setDimensions((prev) => ({ ...prev, [asked.key]: value }));
            }}
            onKeyUp={(e) => {
              if (SLIDER_KEYS.has(e.key)) {
                const value = Number(e.currentTarget.value);
                setDimensions((prev) => ({ ...prev, [asked.key]: value }));
              }
            }}
          />
          <div className="slider-scale">
            <span className="faint">{asked.low}</span>
            {/* The row is three columns (left, value, right); an empty middle keeps the ends at the ends. */}
            <span />
            <span className="faint">{asked.high}</span>
          </div>
        </section>
      )}

      {!expanded && (
        <button className="btn btn-ghost btn-block" onClick={() => setExpanded(true)}>
          Add more detail
        </button>
      )}

      {expanded && (
        <>
          <section className="card stack">
            {rest.map((d) => (
              <div className="field" key={d.key}>
                <label htmlFor={d.key}>
                  {d.label}
                  {dimensions[d.key] === undefined ? (
                    <span className="faint"> — skip if unsure</span>
                  ) : (
                    <>
                      <span className="faint"> · {dimensions[d.key]} </span>
                      {/* A tap now counts, so a stray one must be undoable: back
                          to not said, which saves as blank. */}
                      <button
                        type="button"
                        className="btn-inline"
                        onClick={() =>
                          setDimensions((prev) => {
                            const next = { ...prev };
                            delete next[d.key];
                            return next;
                          })
                        }
                      >
                        clear
                      </button>
                    </>
                  )}
                </label>
                <input
                  id={d.key}
                  className="slider"
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={dimensions[d.key] ?? 3}
                  onChange={(e) =>
                    setDimensions({ ...dimensions, [d.key]: Number(e.target.value) })
                  }
                  onPointerUp={(e) => {
                    const value = Number(e.currentTarget.value);
                    setDimensions((prev) => ({ ...prev, [d.key]: value }));
                  }}
                  onKeyUp={(e) => {
                    if (!SLIDER_KEYS.has(e.key)) return;
                    const value = Number(e.currentTarget.value);
                    setDimensions((prev) => ({ ...prev, [d.key]: value }));
                  }}
                />
                <div className="row-between">
                  <span className="faint">{d.low}</span>
                  <span className="faint">{d.high}</span>
                </div>
              </div>
            ))}
          </section>

          <section className="stack">
            {/*
              NOT "What's going on?", which is the note's question now.

              The chips had that heading first and kept it when the note was
              moved up to second place and relabelled — so one card asked the
              same question twice, of two different inputs, a hundred pixels
              apart, one of them with a curly apostrophe and one with a
              straight one.

              These twelve are conditions, not an account: hungry, no sleep,
              deadline, alone. They are the quick way to say what was true
              without typing it, which is a different question from the one the
              textarea asks, and it is phrased as a question for the reason at
              the top of this file — a question gets answered, a box gets
              skipped.
            */}
            <h2>What else was true?</h2>
            <div className="chips">
              {CONTEXT_TAGS.map((tag) => (
                <button
                  key={tag}
                  className="chip"
                  aria-pressed={tags.includes(tag)}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </section>

        </>
      )}

      {error && <div className="banner banner-error">{error}</div>}

      {/* Beside the saved check-in, never instead of it. The screen stays put so
          the numbers are not on a page the person has already left. */}
      {/*
        CLOSE ACTUALLY CLOSES, and inline it did not.
        
        `onDismiss` was `() => navigate('/')`. On Today that is a navigation to
        the route you are already on, so the button labelled "Close" did
        nothing at all and the card stayed. Finding 2 of the 1 October pass
        asked for this and only its duplicate-row half was done.
        
        Clearing `crisis` also lets the check-in collapse back to its row,
        since the collapsed branch requires `!crisis` — so Today comes back to
        rest, which is what Close should mean. On the /check-in deep link there
        IS somewhere to go, so it still goes.
      */}
      {crisis && (
        <CrisisNoticeCard
          notice={crisis}
          onDismiss={() => {
            setCrisis(null);
            if (!inline) navigate('/');
          }}
        />
      )}

      {/*
        A way back to the one control that is stopping them.

        Refusing to invent a mood was right; leaving somebody who has written a
        paragraph, tapped three chips and dragged Overwhelm to Drowning with a
        dead button 1,357px below the slider was not. Nothing on the screen said
        which control was missing, and leaving discarded the writing.
      */}
      {sliderShown && mood === null && authored && (
        <button
          className="btn btn-ghost btn-block"
          onClick={() => {
            const el = document.getElementById('mood');
            el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            el?.focus();
          }}
        >
          Add a rating at the top to save this
        </button>
      )}

      <div className="stack-sm stack">
        <button
          className="btn btn-primary btn-lg btn-block"
          onClick={() => void save()}
          disabled={busy || moodForSave === null || (inline && last !== null && !authored)}
        >
          {busy ? (
            <span className="spinner" />
          ) : moodForSave === null ? (
            'Move the slider to save'
          ) : inline && last && !authored ? (
            'Add a note or a rating'
          ) : attachTo ? (
            'Add to check-in'
          ) : (
            'Save check-in'
          )}
        </button>
        {!inline && (
          <button className="btn btn-ghost btn-block" onClick={() => navigate('/')} disabled={busy}>
            Not now
          </button>
        )}
      </div>
    </div>
  );
}


